/**
 * Weights Hooks
 *
 * TanStack Query hooks for weight records
 * Updated: 2025-12-08 - Added pagination, getLast, and update hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { weightsApi } from '@/shared/lib/api/weights-api';
import type {
  CreateWeightRequest,
  UpdateWeightRequest,
  WeightsListResponse,
  Weight,
} from '@/shared/types/api.types';

// Query keys
export const weightKeys = {
  all: ['weights'] as const,
  lists: () => [...weightKeys.all, 'list'] as const,
  list: (dogId: string, page?: number) =>
    page ? ([...weightKeys.lists(), dogId, page] as const) : ([...weightKeys.lists(), dogId] as const),
  last: (dogId: string) => [...weightKeys.all, 'last', dogId] as const,
};

/**
 * Hook to get all weight records for a dog (paginated)
 */
export function useWeights(dogId: string | undefined, page = 1, pageSize = 20) {
  return useQuery({
    queryKey: weightKeys.list(dogId ?? '', page),
    queryFn: async (): Promise<WeightsListResponse> => {
      if (!dogId) throw new Error('dogId is required');
      return weightsApi.getAll(dogId, page, pageSize);
    },
    enabled: !!dogId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get the last weight record for a dog
 */
export function useLastWeight(dogId: string | undefined) {
  return useQuery({
    queryKey: weightKeys.last(dogId ?? ''),
    queryFn: async (): Promise<Weight | null> => {
      if (!dogId) throw new Error('dogId is required');
      return weightsApi.getLast(dogId);
    },
    enabled: !!dogId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to create a new weight record
 */
export function useCreateWeight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dogId, data }: { dogId: string; data: CreateWeightRequest }) =>
      weightsApi.create(dogId, data),
    onSuccess: (_data, variables) => {
      // Invalidate weights list and last weight for this dog
      void queryClient.invalidateQueries({
        queryKey: weightKeys.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: weightKeys.last(variables.dogId),
      });
    },
  });
}

/**
 * Hook to update a weight record
 */
export function useUpdateWeight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dogId,
      weightId,
      data,
    }: {
      dogId: string;
      weightId: string;
      data: UpdateWeightRequest;
    }) => weightsApi.update(dogId, weightId, data),
    onSuccess: (_data, variables) => {
      // Invalidate weights list and last weight for this dog
      void queryClient.invalidateQueries({
        queryKey: weightKeys.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: weightKeys.last(variables.dogId),
      });
    },
  });
}

/**
 * Hook to delete a weight record
 */
export function useDeleteWeight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dogId, weightId }: { dogId: string; weightId: string }) =>
      weightsApi.delete(dogId, weightId),
    onSuccess: (_data, variables) => {
      // Invalidate weights list and last weight for this dog
      void queryClient.invalidateQueries({
        queryKey: weightKeys.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: weightKeys.last(variables.dogId),
      });
    },
  });
}
